import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Card, CardContent, CardHeader, Grid, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveUserIntraSquadTournamentPermissions } from "../api/PermissionApiProvider";
import { useDeleteIntraSquadTournament, useListIntraSquadTournamentMatches, useListIntraSquadTournamentTeams, useRetrieveIntraSquadTournament, } from "../api/TournamentApiProvider";
import { useRetrievePlayersByTeamId, useRetrieveTeam } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { SquadCard } from "../teams/SquadCard";
import { TeamColorBox } from "../teams/TeamColorBox";
import { DeleteDialog } from "../utils/DeleteDialog";
import { useNumericParams } from "../utils/useNumericParams";
import { LeagueTable } from "./IntraSquadTournamentLeagueTable";
import { IntraSquadTournamentMatchCard } from "./IntraSquadTournamentMatchCard";
const splitOnCapitals = (s) => s.replace(/([a-z])([A-Z])/g, "$1 $2");
export const IntraSquadTournament = () => {
    const { id } = useNumericParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showDelete, setShowDelete] = React.useState(false);
    const navigate = useNavigate();
    const { data: userTournamentPermissions, isLoading: userTournamentPermissionsIsLoading, error: userTournamentPermissionsError, } = useRetrieveUserIntraSquadTournamentPermissions({ tournamentId: id });
    const { canModify = false, canView = false, canModifyStats = false, } = userTournamentPermissions || {};
    const { data: tournament, isLoading, error, } = useRetrieveIntraSquadTournament({ id, enabled: canView });
    const { data: team, isLoading: isTeamLoading, error: teamError, } = useRetrieveTeam({ id: tournament?.team, enabled: canView });
    const { data: matches, isLoading: isMatchesLoading, error: matchesError, } = useListIntraSquadTournamentMatches({
        tournamentId: id,
        enabled: canView,
    });
    const { data: tournamentTeams, isLoading: isTournamentTeamsLoading, error: tournamentTeamsError, } = useListIntraSquadTournamentTeams({ tournamentId: id, enabled: canView });
    const mutation = useDeleteIntraSquadTournament({
        intraSquadTournament: tournament,
        team,
        onMutate: () => { },
        onSuccess: () => {
            return navigate(`/teams/${tournament?.team}`);
        },
        onError: () => { },
    });
    const { data: players, isLoading: isPlayersLoading, error: playersError, } = useRetrievePlayersByTeamId({
        teamId: team?.id,
        enabled: canView && !!team?.id,
    });
    const composedMap = useMemo(() => {
        if (!players || !tournamentTeams)
            return null;
        return {
            players: new Map(players.map((p) => [p.id, p])),
            tournamentTeams: new Map(tournamentTeams.map((t) => [t.id, t])),
        };
    }, [players, tournamentTeams]);
    const { players: playersMap, tournamentTeams: tournamentTeamsMap } = composedMap || {};
    const handleCloseDeleteDialog = () => {
        setShowDelete(false);
    };
    const handleDelete = () => {
        mutation.mutate();
    };
    const matchesByRound = useMemo(() => {
        return (matches || []).reduce((acc, match) => {
            acc[match.round] = acc[match.round] || [];
            acc[match.round].push(match);
            return acc;
        }, {});
    }, [matches]);
    const loading = userTournamentPermissionsIsLoading ||
        (canView &&
            (isLoading ||
                isTeamLoading ||
                isMatchesLoading ||
                isTournamentTeamsLoading ||
                isPlayersLoading));
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    if (userTournamentPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    if (userTournamentPermissionsError) {
        return _jsx(ErrorView, { error: userTournamentPermissionsError });
    }
    if (!canView) {
        return _jsx(Forbidden, {});
    }
    if (error ||
        teamError ||
        matchesError ||
        tournamentTeamsError ||
        playersError) {
        return (_jsx(ErrorView, { error: error ||
                teamError ||
                matchesError ||
                tournamentTeamsError ||
                new Error("Failed to retrieve tournament data.") }));
    }
    if (loading) {
        return _jsx(Loading, {});
    }
    if (!players ||
        !tournament ||
        !team ||
        !matches ||
        !tournamentTeams ||
        !tournamentTeamsMap ||
        !playersMap) {
        return (_jsx(ErrorView, { error: new Error("Incomplete data for tournament display.") }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(DeleteDialog, { open: showDelete, title: "Delete Intra-Squad Tournament?", onCancel: handleCloseDeleteDialog, onConfirm: handleDelete }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Typography, { variant: "subtitle1", component: "div", align: "center", children: [_jsx("span", { style: { color: "#6c757d" }, children: format(new Date(tournament.startTime), "MMMM do yyyy @ H:mma") }), " ", "\u2022", " ", _jsx(Link, { style: { textDecoration: "none" }, to: `/teams/${team.id}`, children: team.name })] }), action: canModify &&
                            (isMobile ? (_jsxs(Box, { sx: {
                                    minWidth: 0,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                }, children: [_jsx(IconButton, { onClick: handleMenuOpen, children: _jsx(MoreVertIcon, {}) }), _jsx(Menu, { anchorEl: anchorEl, open: open, onClose: handleMenuClose, anchorOrigin: { vertical: "bottom", horizontal: "right" }, transformOrigin: { vertical: "top", horizontal: "right" }, keepMounted: true, children: _jsx(MenuItem, { onClick: () => {
                                                handleMenuClose();
                                                setShowDelete(true);
                                            }, children: _jsx(DeleteIcon, {}) }) })] })) : (_jsx(Stack, { direction: "row", spacing: 1, children: _jsx(IconButton, { onClick: () => setShowDelete(true), children: _jsx(DeleteIcon, {}) }) }))) }), _jsx(CardContent, { children: _jsx(Box, { display: "flex", justifyContent: "center", alignItems: "baseline", flexWrap: "nowrap", sx: { gap: 1, whiteSpace: "nowrap" }, children: _jsx(Typography, { variant: "h5", children: tournament.tournamentName }) }) })] }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { item: true, xs: 12, md: 9, children: [_jsx(LeagueTable, { matches: matches }), _jsx(Grid, { container: true, spacing: 4, children: Object.entries(matchesByRound).map(([round, roundMatches]) => (_jsxs(Grid, { item: true, xs: 12, children: [_jsxs(Typography, { variant: "h6", gutterBottom: true, children: ["Round ", round] }), _jsx(Grid, { container: true, spacing: 2, children: roundMatches.map((match) => (_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(IntraSquadTournamentMatchCard, { match: match, canModify: canModify, canModifyStats: canModifyStats }) }, match.id))) })] }, round))) })] }), _jsxs(Grid, { item: true, xs: 12, md: 3, container: true, spacing: 2, direction: "column", children: [_jsx(Grid, { item: true, children: _jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: "Details" }), _jsx(Stack, { spacing: 1, children: [
                                                    ["Type", splitOnCapitals(tournament.tournamentType)],
                                                    ["Half Length", `${tournament.halfLength} mins`],
                                                    ["Half Time", `${tournament.halfTimeLength} mins`],
                                                    ["Break Between Matches", `${tournament.breakLength} mins`],
                                                ].map(([label, value]) => (_jsxs(Box, { display: "flex", justifyContent: "space-between", children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), _jsx(Typography, { variant: "body2", children: value })] }, label))) })] }) }) }), _jsx(Grid, { item: true, children: _jsx(Typography, { variant: "h6", gutterBottom: true, children: "Teams" }) }), tournamentTeams.map((tournamentTeam, i) => (_jsx(Grid, { item: true, children: _jsx(SquadCard, { title: _jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [_jsx(TeamColorBox, { color: tournamentTeam.primaryColour, onClick: () => { } }), _jsx(Typography, { component: "span", variant: "body2", children: tournamentTeam.name })] }), players: tournamentTeam.players.map((summary) => playersMap.get(summary.id) ?? summary) }) }, i)))] })] })] }));
};
